/* eslint-disable no-control-regex */
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import {
    ScrollTextIcon, ShieldAlertIcon, RefreshCwIcon, DownloadIcon,
    SearchIcon, XIcon, AlertTriangleIcon, AlertCircleIcon,
    InfoIcon, BugIcon, CheckCircleIcon, ChevronsDownIcon,
    FileTextIcon, Loader2Icon,
} from 'lucide-react';
import { useBackendApi } from '@/hooks/fetch';
import { openExternalLink } from '@/lib/navigation';

// ── types ──────────────────────────────────────────────────────────────────
type SystemLogPageProps = { pageName: 'console' | 'action' };
type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'success' | 'plain';
type FilterTab = 'all' | LogLevel;

type LogLine = {
    id: number;
    level: LogLevel;
    text: string;
};

// ── helpers ────────────────────────────────────────────────────────────────
const ANSI_RE = /\x1B(?:[@-Z\\-_]|\[[0-?]*[ -/]*[@-~])/g;
const stripAnsi = (s: string) => s.replace(ANSI_RE, '');

function classifyLine(t: string): LogLevel {
    const l = t.toLowerCase();
    if (/\[error\]|error:|^\s*err\b|\berror\b/.test(l)) return 'error';
    if (/\[warn(ing)?\]|warn(ing)?:/.test(l))            return 'warn';
    if (/\[success\]|started|connected|ready/.test(l))   return 'success';
    if (/\[info\]|info:/.test(l))                        return 'info';
    if (/\[debug\]|debug:/.test(l))                      return 'debug';
    return 'plain';
}

// ── level config ───────────────────────────────────────────────────────────
const LEVEL: Record<LogLevel, {
    dot: string; text: string; bg: string;
    icon: React.ElementType; label: string;
}> = {
    error:   { dot: '#ef4444', text: 'text-red-400',     bg: 'bg-red-500/[0.06]',      icon: AlertCircleIcon,   label: 'Error'   },
    warn:    { dot: '#fbbf24', text: 'text-amber-300',   bg: 'bg-amber-500/[0.05]',    icon: AlertTriangleIcon, label: 'Warn'    },
    info:    { dot: '#60a5fa', text: 'text-blue-300',    bg: 'bg-blue-500/[0.04]',     icon: InfoIcon,          label: 'Info'    },
    debug:   { dot: '#475569', text: 'text-slate-500',   bg: 'transparent',            icon: BugIcon,           label: 'Debug'   },
    success: { dot: '#00ff66', text: 'text-[#00ff66]',   bg: 'bg-[#00ff66]/[0.03]',   icon: CheckCircleIcon,   label: 'Success' },
    plain:   { dot: '#334155', text: 'text-slate-300',   bg: 'transparent',            icon: FileTextIcon,      label: 'Other'   },
};

const FILTER_ORDER: FilterTab[] = ['all', 'error', 'warn', 'info', 'success', 'debug', 'plain'];

// ── component ──────────────────────────────────────────────────────────────
export default function SystemLogPage({ pageName }: SystemLogPageProps) {
    const [lines, setLines]           = useState<LogLine[]>([]);
    const [isLoading, setIsLoading]   = useState(true);
    const [loadError, setLoadError]   = useState('');
    const [search, setSearch]         = useState('');
    const [filter, setFilter]         = useState<FilterTab>('all');
    const [autoScroll, setAutoScroll] = useState(true);
    const [showSearch, setShowSearch] = useState(false);
    const bottomRef   = useRef<HTMLDivElement>(null);
    const scrollRef   = useRef<HTMLDivElement>(null);
    const searchRef   = useRef<HTMLInputElement>(null);

    const isConsole = pageName === 'console';
    const pageTitle    = isConsole ? 'Console Log'  : 'Action Log';
    const pageSubtitle = isConsole
        ? 'txAdmin parent terminal output — debug messages included'
        : 'Record of all admin actions and server events';
    const PageIcon = isConsole ? ScrollTextIcon : ShieldAlertIcon;
    const accentColor = isConsole ? '#00ffd1' : '#a78bfa';

    const getLogsApi = useBackendApi<{ data: string }>({
        method: 'GET',
        path: `/systemLog/${pageName}`,
        throwGenericErrors: true,
    });

    const load = useCallback(() => {
        setIsLoading(true);
        setLoadError('');
        getLogsApi({
            success: (resp) => {
                const raw = resp.data ?? '';
                const parsed: LogLine[] = raw
                    .split(/\r?\n/)
                    .filter(l => l.trim().length > 0)
                    .slice(-3000)  // cap at last 3000 lines
                    .map((l, i) => {
                        const text = stripAnsi(l);
                        return { id: i, level: classifyLine(text), text };
                    });
                setLines(parsed);
                setIsLoading(false);
            },
            error: (msg) => {
                setLoadError(msg);
                setIsLoading(false);
            },
        });
    }, [pageName]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    // auto-scroll
    useEffect(() => {
        if (autoScroll && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [lines, autoScroll]);

    // open search
    useEffect(() => {
        if (showSearch) setTimeout(() => searchRef.current?.focus(), 50);
    }, [showSearch]);

    // stats
    const stats = useMemo(() => {
        const counts: Record<LogLevel, number> = { error: 0, warn: 0, info: 0, debug: 0, success: 0, plain: 0 };
        for (const l of lines) counts[l.level]++;
        return counts;
    }, [lines]);

    // filtered + searched
    const visible = useMemo(() => {
        let out = lines;
        if (filter !== 'all') out = out.filter(l => l.level === filter);
        if (search.trim()) {
            const q = search.toLowerCase();
            out = out.filter(l => l.text.toLowerCase().includes(q));
        }
        return out;
    }, [lines, filter, search]);

    // detect manual scroll away from bottom
    const handleScroll = () => {
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        setAutoScroll(scrollHeight - scrollTop - clientHeight < 80);
    };

    const highlightSearch = (text: string) => {
        if (!search.trim()) return <span>{text}</span>;
        const q = search.trim();
        const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
        return (
            <>
                {parts.map((p, i) =>
                    p.toLowerCase() === q.toLowerCase()
                        ? <mark key={i} className="bg-[#fbbf24]/25 text-[#fbbf24] rounded-sm px-0.5">{p}</mark>
                        : <span key={i}>{p}</span>
                )}
            </>
        );
    };

    return (
        <div className="flex flex-col h-full w-full bg-[#030507] border border-white/[0.06] md:rounded-xl overflow-hidden shadow-[0_0_50px_rgba(0,255,209,0.03)]">

            {/* ── Header ── */}
            <div className="shrink-0 flex items-start justify-between gap-4 px-5 py-3.5 border-b border-white/[0.05] bg-[#030507]/95">
                <div className="flex items-center gap-3 min-w-0">
                    <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border"
                        style={{ background: `${accentColor}08`, borderColor: `${accentColor}20` }}
                    >
                        <PageIcon size={15} style={{ color: accentColor }} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h1 className="font-mono text-sm font-bold tracking-wider text-slate-100 uppercase">
                                {pageTitle}
                            </h1>
                            {/* stats chips */}
                            {!isLoading && stats.error > 0 && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-red-500/5 border-red-500/20 text-red-400 font-mono uppercase tracking-widest">
                                    {stats.error} err
                                </span>
                            )}
                            {!isLoading && stats.warn > 0 && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border bg-amber-500/5 border-amber-400/20 text-amber-300 font-mono uppercase tracking-widest">
                                    {stats.warn} warn
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5 font-mono truncate">{pageSubtitle}</p>
                    </div>
                </div>

                {/* action buttons */}
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        onClick={() => setShowSearch(v => !v)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-semibold font-mono uppercase tracking-wider transition-all duration-150 ${
                            showSearch
                                ? 'bg-[#00ffd1]/8 border-[#00ffd1]/25 text-[#00ffd1]'
                                : 'bg-white/[0.02] border-white/[0.06] text-slate-500 hover:border-[#00ffd1]/20 hover:text-[#00ffd1] hover:bg-[#00ffd1]/5'
                        }`}
                    >
                        <SearchIcon size={12} />
                        <span className="hidden sm:inline">Search</span>
                    </button>
                    <button
                        onClick={load}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-white/[0.02] border-white/[0.06] text-slate-500 hover:border-[#00ffd1]/20 hover:text-[#00ffd1] hover:bg-[#00ffd1]/5 text-[11px] font-semibold font-mono uppercase tracking-wider transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <RefreshCwIcon size={12} className={isLoading ? 'animate-spin' : ''} />
                        <span className="hidden sm:inline">Reload</span>
                    </button>
                    <button
                        onClick={() => openExternalLink(`/systemLog/${pageName}`)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-white/[0.02] border-white/[0.06] text-slate-500 hover:border-white/10 hover:text-slate-300 hover:bg-white/[0.03] text-[11px] font-semibold font-mono uppercase tracking-wider transition-all duration-150"
                    >
                        <DownloadIcon size={12} />
                        <span className="hidden sm:inline">Raw</span>
                    </button>
                </div>
            </div>

            {/* ── Search + Filter bar ── */}
            {showSearch && (
                <div className="shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-4 py-2.5 border-b border-white/[0.04] bg-black/20">
                    {/* search input */}
                    <div className="relative flex-1 min-w-0">
                        <SearchIcon size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 pointer-events-none" />
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search log lines..."
                            className="w-full bg-white/[0.03] border border-white/[0.07] rounded-lg pl-8 pr-8 py-1.5 text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-[#00ffd1]/30 focus:ring-1 focus:ring-[#00ffd1]/15 transition-all"
                        />
                        {search && (
                            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400">
                                <XIcon size={11} />
                            </button>
                        )}
                    </div>

                    {/* level filter tabs */}
                    <div className="flex items-center gap-1 flex-wrap">
                        {FILTER_ORDER.map(f => {
                            const count = f === 'all' ? lines.length : stats[f as LogLevel];
                            const cfg = f === 'all' ? null : LEVEL[f as LogLevel];
                            const active = filter === f;
                            return (
                                <button
                                    key={f}
                                    onClick={() => setFilter(f)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-bold font-mono uppercase tracking-wider transition-all duration-100 ${
                                        active
                                            ? 'bg-white/[0.06] border-white/15 text-white'
                                            : 'bg-transparent border-white/[0.05] text-slate-600 hover:text-slate-300 hover:border-white/10'
                                    }`}
                                >
                                    {cfg && (
                                        <span
                                            className="w-1.5 h-1.5 rounded-full shrink-0"
                                            style={{ background: cfg.dot }}
                                        />
                                    )}
                                    {f}
                                    {count > 0 && (
                                        <span className="text-slate-600 font-normal">({count})</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Log content ── */}
            <div className="relative flex-1 overflow-hidden">

                {/* Loading overlay */}
                {isLoading && (
                    <div className="absolute inset-0 z-20 bg-[#030507]/80 flex items-center justify-center backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-4">
                            <div className="relative">
                                <Loader2Icon className="w-10 h-10 animate-spin text-[#00ffd1]/50" />
                                <div className="absolute inset-0 rounded-full blur-xl bg-[#00ffd1]/8" />
                            </div>
                            <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">
                                loading {pageTitle.toLowerCase()}...
                            </span>
                        </div>
                    </div>
                )}

                {/* Error overlay */}
                {loadError && !isLoading && (
                    <div className="absolute inset-0 z-20 bg-[#030507]/80 flex flex-col items-center justify-center gap-4 backdrop-blur-sm">
                        <div className="w-10 h-10 rounded-xl bg-destructive/10 border border-destructive/20 flex items-center justify-center">
                            <AlertCircleIcon size={20} className="text-destructive" />
                        </div>
                        <div className="text-center max-w-sm">
                            <p className="text-sm font-semibold text-slate-200 mb-1">Failed to load {pageTitle}</p>
                            <p className="text-xs font-mono text-slate-500">{loadError}</p>
                        </div>
                        <button
                            onClick={load}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#00ffd1]/8 border border-[#00ffd1]/20 text-[#00ffd1] text-xs font-semibold font-mono uppercase tracking-wider hover:bg-[#00ffd1]/12 transition-all"
                        >
                            <RefreshCwIcon size={12} /> Retry
                        </button>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !loadError && visible.length === 0 && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-slate-600">
                        <FileTextIcon size={32} className="opacity-30" />
                        <span className="text-xs font-mono uppercase tracking-widest">
                            {lines.length === 0 ? 'No log data available' : 'No lines match your filter'}
                        </span>
                    </div>
                )}

                {/* Log lines */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-white/[0.06] hover:scrollbar-thumb-white/10 scrollbar-track-transparent font-mono text-[11px] leading-relaxed"
                >
                    <div className="py-1">
                        {visible.map((line, idx) => {
                            const cfg = LEVEL[line.level];
                            return (
                                <div
                                    key={line.id}
                                    className={`group flex items-start gap-0 hover:bg-white/[0.015] transition-colors duration-75 ${cfg.bg}`}
                                >
                                    {/* line number */}
                                    <span className="shrink-0 w-12 text-right pr-3 pt-[3px] text-slate-700 text-[10px] select-none border-r border-white/[0.03] mr-3">
                                        {idx + 1}
                                    </span>
                                    {/* level dot */}
                                    <span
                                        className="shrink-0 w-1.5 h-1.5 rounded-full mt-[5px] mr-2.5"
                                        style={{ background: cfg.dot, boxShadow: line.level !== 'plain' && line.level !== 'debug' ? `0 0 6px ${cfg.dot}60` : undefined }}
                                    />
                                    {/* text */}
                                    <span className={`${cfg.text} break-all whitespace-pre-wrap flex-1 pr-4 py-[2px]`}>
                                        {highlightSearch(line.text)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div ref={bottomRef} />
                </div>

                {/* Scroll to bottom */}
                {!autoScroll && (
                    <button
                        onClick={() => {
                            setAutoScroll(true);
                            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="absolute bottom-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#030507] border border-[#00ffd1]/20 text-[#00ffd1]/70 text-[10px] font-mono font-semibold uppercase tracking-wider shadow-lg hover:border-[#00ffd1]/40 hover:text-[#00ffd1] transition-all"
                    >
                        <ChevronsDownIcon size={13} className="animate-bounce" />
                        Jump to bottom
                    </button>
                )}
            </div>

            {/* ── Footer ── */}
            {!isLoading && !loadError && lines.length > 0 && (
                <div className="shrink-0 flex items-center justify-between px-4 py-1.5 border-t border-white/[0.04] bg-black/20">
                    <span className="text-[9px] font-mono text-slate-600 uppercase tracking-widest">
                        {visible.length.toLocaleString()} / {lines.length.toLocaleString()} lines
                        {filter !== 'all' && ` · filtered by ${filter}`}
                        {search && ` · "${search}"`}
                    </span>
                    <span className="text-[9px] font-mono text-slate-700 uppercase tracking-widest">
                        Ctrl+F to search · F5 to reload
                    </span>
                </div>
            )}
        </div>
    );
}
